import React, { useMemo, useContext } from 'react';

import AttributeGroupSingleSelectFacet from '@/client/components/FilterSidebar/molecules/AttributeGroupSingleSelectFacet';
import AttributeGroupMultiSelectFacet from '@/client/components/FilterSidebar/molecules/AttributeGroupMultiSelectFacet';
import CategoryTreeFacet from '@/client/components/FilterSidebar/molecules/CategoryTreeFacet';
import AttributeRangeFacet from '@/client/components/FilterSidebar/molecules/AttributeRangeFacet';
import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import { RowHeader } from '@hz-design-system/web-ui';

interface RegularFilterProps {
  facets: TFacet[];
  title?: string;
  isMobile: boolean;
  searchRequestObject: TSearchRequestObject;
  totalResults: number;
  sharedDomainCookie?: string;
  searchDistanceOptions?: { name: string; value: number }[];
}

const RegularFilters: React.FC<RegularFilterProps> = ({
  facets,
  title,
  isMobile,
  searchRequestObject,
  totalResults,
}) => {
  const { tenantContext } = useContext(EnvironmentContext);

  // Filter category tree facets to show only relevant categories based on current path
  const filteredFacets = useMemo(() => {
    if (!facets || tenantContext?.tenant !== 'mp') {
      return facets ?? [];
    }

    return facets.map((facet) => {
      if (facet.type === 'CategoryTreeFacet' && facet.key === 'RelevantCategories') {
        const l1Category = searchRequestObject?.categories?.l1Category;
        const l2Category = searchRequestObject?.categories?.l2Category;

        // No filtering needed if no category is selected in the path
        if (!l1Category && !l2Category) {
          return facet;
        }

        const filteredCategories = facet.categories?.filter((cat) => {
          if (l2Category) {
            // In L2 context: show the L1 parent and the current L2
            return cat.id === l1Category?.id || cat.id === l2Category.id;
          }
          if (l1Category) {
            // In L1 context: show the selected L1 and its children
            return cat.id === l1Category.id || cat.parentId === l1Category.id;
          }
          return cat.selected;
        });

        // Fallback to original categories if filtering results in empty array
        // This prevents users from getting stuck when API doesn't return expected data
        const categoriesToShow =
          filteredCategories && filteredCategories.length > 0 ? filteredCategories : facet.categories;

        return {
          ...facet,
          categories: categoriesToShow,
        };
      }
      return facet;
    });
  }, [facets, searchRequestObject?.categories, tenantContext?.tenant]);

  return (
    <>
      {title && <RowHeader title={title} />}
      {filteredFacets.map((facet) => {
        const { key, attributeGroup, label, range, categories, singleSelect, type } = facet;

        switch (type) {
          case 'AttributeGroupFacet':
            if (singleSelect) {
              return (
                <AttributeGroupSingleSelectFacet
                  key={key}
                  label={label ?? ''}
                  attributeGroup={attributeGroup || []}
                  inputKey={key}
                  searchRequestObject={searchRequestObject}
                />
              );
            }
            return (
              <AttributeGroupMultiSelectFacet
                key={key}
                label={label ?? ''}
                attributeGroup={attributeGroup || []}
                inputKey={key}
                searchRequestObject={searchRequestObject}
                totalResults={totalResults}
              />
            );
          case 'AttributeRangeFacet':
            return (
              <AttributeRangeFacet
                key={key}
                label={label ?? ''}
                inputKey={key}
                range={range || { from: 0, to: 0 }}
                searchRequestObject={searchRequestObject}
              />
            );
          case 'CategoryTreeFacet':
            return (
              <CategoryTreeFacet
                key={key}
                categories={categories ?? []}
                inputKey={key}
                label={label ?? ''}
                isMobile={isMobile}
                searchRequestObject={searchRequestObject}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

export default RegularFilters;
